/**
 * Mobile UX floor — viewport meta tag + iOS visualViewport keyboard tracking.
 *
 * Two responsibilities:
 *   1. Ensure the host page's <meta name="viewport"> includes
 *      viewport-fit=cover (required for env(safe-area-inset-*) to work)
 *      and interactive-widget=resizes-content (Android keyboard
 *      auto-shrinks the layout viewport — free keyboard avoidance).
 *   2. Track the iOS keyboard via visualViewport.resize and expose its
 *      height as the --sp-kb-offset CSS custom property on <html>. CSS
 *      uses it to park fixed-position widget chrome above the keyboard.
 *
 * Idempotent — safe to call multiple times. Returns a teardown for
 * tests; production never tears down.
 */

const VIEWPORT_FLAGS_TO_ENSURE = [
  'viewport-fit=cover',
  'interactive-widget=resizes-content',
];

const KB_OFFSET_VAR = '--sp-kb-offset';
const VV_TOP_VAR = '--sp-vv-top';
const VV_HEIGHT_VAR = '--sp-vv-h';

/**
 * The three CSS custom properties the keyboard tracker publishes, from the
 * window and visual-viewport numbers. Pure, so the iOS cases are testable:
 *   kbOffset — keyboard height (innerHeight − visual height), floored at 0
 *   vvTop    — the visual viewport's offset into the scrolled layout viewport
 *   vvHeight — the visual viewport's height
 */
export function keyboardVars(
  innerHeight: number,
  vvHeight: number,
  vvOffsetTop: number,
): { kbOffset: string; vvTop: string; vvHeight: string } {
  // Floor at 0 — a small negative can appear briefly during rotation.
  const offset = Math.max(0, Math.round(innerHeight - vvHeight));
  return {
    kbOffset: `${offset}px`,
    vvTop: `${Math.max(0, Math.round(vvOffsetTop))}px`,
    vvHeight: `${Math.max(0, Math.round(vvHeight))}px`,
  };
}

let installed = false;

/**
 * Ensure the document has a viewport meta tag with the flags we need.
 * Augments an existing meta tag rather than replacing it, so the host
 * page's width=device-width / initial-scale=1 etc. are preserved.
 */
function ensureViewportMeta(): void {
  if (typeof document === 'undefined') return;

  let meta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'viewport';
    meta.content =
      'width=device-width, initial-scale=1, viewport-fit=cover, interactive-widget=resizes-content';
    document.head.appendChild(meta);
    return;
  }

  const current = (meta.content || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  let changed = false;
  for (const flag of VIEWPORT_FLAGS_TO_ENSURE) {
    const [key] = flag.split('=');
    const has = current.some((f) => f.split('=')[0]?.trim() === key);
    if (!has) {
      current.push(flag);
      changed = true;
    }
  }
  if (changed) meta.content = current.join(', ');
}

/**
 * Track keyboard via visualViewport. Writes --sp-kb-offset (in px) to
 * the document root every time the visual viewport resizes. CSS reads
 * it to lift fixed bottom-anchored elements above the keyboard.
 */
function trackKeyboard(): () => void {
  if (typeof window === 'undefined' || !window.visualViewport) {
    return () => undefined;
  }

  const vv = window.visualViewport;
  const root = document.documentElement;

  const update = (): void => {
    const v = keyboardVars(window.innerHeight, vv.height, vv.offsetTop);
    root.style.setProperty(KB_OFFSET_VAR, v.kbOffset);
    // iOS Safari scrolls the LAYOUT viewport to reveal a focused field, so a
    // position:fixed sheet anchored top/bottom moves with it (David's iPhone
    // photos, 2026-10-21: composer under the keyboard bar, or a 45px gap).
    // The visual viewport's own top + height are the only honest anchors.
    root.style.setProperty(VV_TOP_VAR, v.vvTop);
    root.style.setProperty(VV_HEIGHT_VAR, v.vvHeight);
  };

  vv.addEventListener('resize', update);
  vv.addEventListener('scroll', update);
  update();

  return () => {
    vv.removeEventListener('resize', update);
    vv.removeEventListener('scroll', update);
    root.style.removeProperty(KB_OFFSET_VAR);
    root.style.removeProperty(VV_TOP_VAR);
    root.style.removeProperty(VV_HEIGHT_VAR);
  };
}

/**
 * Install the mobile UX floor. Safe to call from boot() on every mount —
 * subsequent calls are no-ops.
 */
export function installMobileFloor(): () => void {
  if (installed) return () => undefined;
  installed = true;

  ensureViewportMeta();
  const teardownKb = trackKeyboard();

  return () => {
    installed = false;
    teardownKb();
  };
}
